/**
 * toastStore.js — Tiny ephemeral notification queue.
 *
 * Toasts are tagged success | info | warn | error and self-dismiss after a
 * timeout (default 4s). Anything in the app can call:
 *
 *   import toast from './toastStore.js';
 *   toast.success('Saved!');
 *   toast.error('Persist failed', { detail: err.message });
 *
 * The <Toasts /> component subscribes via `state.toasts` and renders the
 * stack in the bottom-left corner.
 */
import { createStore, produce } from 'solid-js/store';

const [state, setState] = createStore({ toasts: [] });

let _idSeq = 0;
const _timers = new Map();

function _push(kind, msg, opts) {
  _idSeq += 1;
  const id = _idSeq;
  const ttl = opts?.ttl ?? (kind === 'error' ? 6000 : 4000);
  const t = {
    id,
    kind,
    msg: String(msg ?? ''),
    detail: opts?.detail ? String(opts.detail) : '',
    createdAt: Date.now(),
  };
  setState('toasts', list => [...list, t]);
  if (ttl > 0) {
    _timers.set(id, setTimeout(() => dismiss(id), ttl));
  }
  return id;
}

function dismiss(id) {
  clearTimeout(_timers.get(id));
  _timers.delete(id);
  setState('toasts', list => list.filter(t => t.id !== id));
}

export const toast = {
  state,
  dismiss,
  success: (msg, opts) => _push('success', msg, opts),
  info:    (msg, opts) => _push('info',    msg, opts),
  warn:    (msg, opts) => _push('warn',    msg, opts),
  error:   (msg, opts) => _push('error',   msg, opts),
};

export default toast;
